import { StepType } from '../enums/step-type.enum';
import { StepParameter, ModuleParameter, StaticParameter, StepOutputParameter } from '../models/parameter.model';
import { Step } from '../models/step.model';
import { ModuleInputType } from '../enums/module-input-type.enum';

/**
 * Connects a step to another one. Means, it creates a new StepOutputParameter (this holds the connection) and adds it to the targetStep.
 * Then, if successfully connected, we need to reorganize the parameters (ONLY for system modules).
 * By reorganizing the parameters we cover the following cases (assumption: DefineInput is not connected yet):
 *  - DefineInput has one file. Then, we connect the DefineInput to a list_file parameter. -> File needs to be moved from file to list_file parameter.
 *  - DefineInput has several files. Then, we connect DefineInput to a file parameter. -> Files need to be moved from list_file to file (batach run)
 *
 * @param targetInputParamKey - the input key of the target step to connect to
 */
export function connectTo(moduleParameter: ModuleParameter, targetInputParamKey: string, targetStep: Step, sourceStep: Step): boolean {
    // create connection
    const param: StepOutputParameter = new StepOutputParameter(sourceStep.flowId, moduleParameter.type, moduleParameter.key, targetInputParamKey);
    const successfullyConnected: boolean = targetStep.setInputParameter(param);

    // Reorganize parameters
    if ((successfullyConnected === true && sourceStep.type === StepType.ModuleDefineInput) || sourceStep.type === StepType.WorkflowDefineInput) {
        const stepParam: StepParameter | undefined = targetStep.getParameter(targetInputParamKey);
        if (stepParam != null && stepParam instanceof StepOutputParameter) {
            const stepType: ModuleInputType = stepParam.type;

            let fileParam: StaticParameter<ModuleParameter, string | string[]> | undefined = sourceStep.getParameter('file') as StaticParameter<
                ModuleParameter,
                string | string[]
            >;

            let fileListParam: StaticParameter<ModuleParameter, string[]> | undefined = sourceStep.getParameter('file_list') as StaticParameter<
                ModuleParameter,
                string[]
            >;

            // Case: Several URLs are in File parameter. On connecting to file list we need to move them from file to file_list
            if (stepType === ModuleInputType.ListFile) {
                if (fileListParam == null) {
                    fileListParam = new StaticParameter(moduleParameter, []);
                    sourceStep.setInputParameter(fileListParam);
                }

                // If a fileParam was defined, take over the value and write it into file_list parameter
                if (fileParam != null) {
                    const paramValue: string | string[] | undefined = fileParam.getValue();

                    // 274183: A WorkflowDefineInput and ModuleDefineInput is generated initially with its default values from spec
                    // (which is file parameter with "" as value) (Code: Step.getDefaultValueOutOfSpec()). When we move this value
                    // from file to file_list parameter, it will put the empty string into an array in the file_list parameter,
                    // which will indicate that we have one file selected (""). This is wrong, so we need to filter out empty strings here.
                    if (paramValue != null && paramValue !== '') {
                        if (Array.isArray(paramValue)) {
                            fileListParam.value = paramValue;
                        } else if (typeof paramValue === 'string') {
                            fileListParam.value = [paramValue];
                        }
                        sourceStep.removeInputParameter('file');
                    }
                }
            } else if (stepType === ModuleInputType.File) {
                // Case: URLs are in file_list. On connecting to file we need to move them from file_list to file (batch run)
                // ATTENTION: This can only happen for WorkflowDefineInput. But we don't need a validation here, since the
                // validation is already done before creating the connection (check editorVisualizerService.isValidConnection())

                if (fileParam == null) {
                    fileParam = new StaticParameter(moduleParameter, fileListParam.getValue());
                    sourceStep.setInputParameter(fileParam);
                }

                if (fileListParam != null) {
                    const paramValue: string | string[] | undefined = fileListParam.getValue();
                    if (paramValue != null && ((Array.isArray(paramValue) && paramValue.length > 0) || typeof paramValue === 'string')) {
                        fileParam.value = paramValue;
                    }
                    sourceStep.removeInputParameter('file_list');
                }
            }
        }
    }

    return successfullyConnected;
}
